import clsx from "clsx";
import { AnimatePresence } from "framer-motion";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Seomatic } from "react-seomatic";

import BodyText from "@components/BodyText";
import ContentSections from "@components/ContentSections";
import EmbeddedForm from "@components/EmbeddedForm";
import FileDownloadButtons from "@components/FileDownloadButtons";
import Heading from "@components/Heading";
import Message from "@components/Message";
import PodcastLogoBlock from "@components/PodcastLogoBlock";
import SecondaryButton from "@components/SecondaryButton";
import SidebarCalloutsComponent from "@components/SidebarCallouts";
import Speakers from "@components/Speakers";

import { PreviewContent } from "@interfaces/PreviewContent.interface";
import fetchForm from "@lib/fetchForm";
import { returnStaticProps } from "@lib/returnStaticProps";
import craft from "@pages/api/craft";
import QueryFetchRelatedResources from "@queries/QueryFetchRelatedResources.graphql";
import QuerySlugs from "@queries/QuerySlugs.graphql";
import QueryResource from "@queries/resources/QueryResource.graphql";

import Spacer from "@abnormal/components/Spacer";
import { PLASMIC } from "@abnormal/plasmic-init";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
  Stack
} from "@chakra-ui/react";
import { headerHeight } from "@lib/headerHeight";
import getHref from "@lib/routes";
import {
  ComponentRenderData,
  PlasmicComponent,
  PlasmicRootProvider,
  extractPlasmicQueryData
} from "@plasmicapp/loader-nextjs";
import Image from "next/image";

export const getStaticPaths: GetStaticPaths = async (context) => {
	// Fetch every possible path
	const response = await craft(QuerySlugs, {
		section: "resources",
		limit: 20
	});

	const paths = [];

	response.entries.map(async (resource) => {
		paths.push({
			params: { slug: resource.slug }
		});
	});

	return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  previewData: nextPreviewData,
  preview
}) => {
  // For Craft preview capability
  const previewData: PreviewContent = nextPreviewData as PreviewContent;

  // Construct uri from the slug array
  const { slug } = params;

  // What is the uri
  const uri = `/resources/${slug}`;

  const plasmicData = await PLASMIC.maybeFetchComponentData([
    "BladesDynamicDemoBlade",
    "BladesDynamicRelatedContentManualEntries",
    "Footer"
  ], {
    // deferChunks: true,
  });
  if (!plasmicData) {
    throw new Error("No Plasmic design found");
  }

  // Fetch the page's data
  const response = await craft(
    QueryResource,
    { slug },
    preview ? previewData.token : null
  );

  const {
    pageEntry,
    resource,
    primaryNavigation,
    primaryCallToActions,
    persistentNav
  } = response;

  // Set headerTheme
  const headerTheme = resource?.overrideDefaultHeaderTheme
    ? resource?.headerTheme || "dark"
    : pageEntry?.headerTheme || "dark";

  // Build related resources
  let filteredResources = null;

  if (!!resource && resource.topics?.length) {
    const relatedResponse = await craft(QueryFetchRelatedResources, {
      categorySlug: resource.topics[0].slug
    });
    const { relatedResources } = relatedResponse;

    filteredResources = relatedResources.filter((e) => e.id !== resource.id);
  }

  // Cache the necessary Plasmic data fetched for the page
  const queryCache = await extractPlasmicQueryData(
    <PlasmicRootProvider loader={PLASMIC} prefetchedData={plasmicData}>
      <PlasmicComponent
        component="BladesDynamicDemoBlade"
      />
      {!!filteredResources && (
        <PlasmicComponent
          component="BladesDynamicRelatedContentManualEntries"
          componentProps={{ entries: filteredResources }}
          projectId={process.env.NEXT_PUBLIC_PLASMIC_DESIGN_SYSTEM_PROJECT_ID}
        />
      )}
    </PlasmicRootProvider>
  );

  /*
    Fetch the form and pass form data as props
  */
  let formData = null;

  let formId = null;

  let formEntry = null;

  let isGated = false;

  // Use the form id specified by the selected form in the pageEntry (if there is one)
  if (resource?.gatedResource) {
    isGated = true;

    if (!!resource && resource.form?.length) {
      formId = resource.form[0].formDetails[0].formId;
      formEntry = resource.form[0];
    } else {
      formId = pageEntry.form ? pageEntry.form[0].formDetails[0].formId : null;
      formEntry = pageEntry.form[0];
    }

    if (formId) {
      formData = await fetchForm({ formId: formId });
    }
  }

  // Return props if entry exists, otherwise check Retour for redirects or 404
  const returnResponse = await returnStaticProps(
    {
      navigation: {
        primaryNavigation,
        primaryCallToActions
      },
      pageEntry,
      persistentNav,
      resource,
      formData,
      formEntry,
      filteredResources,
      headerTheme,
      isGated,
      plasmicData,
      queryCache
    },
    !resource || (pageEntry.status == "disabled" && !preview),
    uri
  );

  return returnResponse;
};

interface IProps {
  pageEntry: any;
  resource: any;
  formData: any;
  formEntry: any;
  filteredResources: any;
  headerTheme: string;
  isGated: boolean;
  plasmicData: ComponentRenderData;
  queryCache: Record<string, any>;
}

export default function Page({
  pageEntry,
  resource,
  formData,
  formEntry,
  filteredResources,
  headerTheme,
  isGated,
  plasmicData,
  queryCache
}: IProps): JSX.Element {
  const router = useRouter();
  const [accessGranted, setAccessGranted] = useState(false);
  let manualAuth = false;

  // Redirect to custom landing page if applicable
  useEffect(() => {
    if (resource?.linkDestination && resource.entryTarget?.url) {
      router.push(resource.entryTarget.url);
    }
  }, [router, resource]);

  const columnWidths = {
    heading: resource.imageSizeToggle ? 735 : 750,
    headingRight: resource.imageSizeToggle ? 430 : 363,
    left: 775,
    right: 363
  };

  const formValues = isGated && {...formEntry.formDetails[0]};

  if (resource.overrideDefaultFormValues) {
    Object.keys(resource.formDetails[0]).map((key, index) => {
      if (Object.values(resource.formDetails[0])[index]) {
        formValues[key] = Object.values(resource.formDetails[0])[index];
      }
    });
  }

  useEffect(() => {
    if (router.query.bypass === "true") {
      setAccessGranted(true);
      manualAuth = true;
    }
  });

  const handleFormSuccess = () => {
    router.push(
      {
        pathname: `/${resource.uri}/success`,
        query: { success: true }
      },
      `/${resource.uri}/success`
    );
  };

  // Determine which content sections to show - REMOVE WHEN THANK YOU PAGES READY
  const visibleBeforeAccessSections = resource.contentSections.filter(
    (s) => !s.visibleAfterAccess
  );

  const visibleAfterAccessSections = resource.contentSections.filter(
    (s) => s.visibleAfterAccess
  );

  const allSections = resource.contentSections;

  let resourceType = "downloadable";

  if (
    resource.resourceTypes[0]?.slug == "webinars" ||
    resource.resourceTypes[0]?.slug == "videos"
  ) {
    resourceType = "video";
  }

  const breadcrumbHref = getHref(pageEntry);

  return (
    <PlasmicRootProvider
      loader={PLASMIC}
      prefetchedData={plasmicData}
      prefetchedQueryData={queryCache}
      pageQuery={router.query}
    >
      <Seomatic Head={Head} {...resource.seomatic} />
      <Box className="pageContent bg-white-01">
        {/* Entry Header */}
        <Box
          backgroundColor={resource?.showHeroImage
            ? "transparent"
            : headerTheme === "dark" ? "purple-gray-02" : "purple-02"
          }
          backgroundImage={resource?.showHeroImage && {
            base: resource?.mobileHeroImage[0]?.url ? resource?.mobileHeroImage[0]?.url : resource?.heroImage[0]?.url,
            lg: resource?.heroImage[0]?.url
          }}
          backgroundPosition="center"
          backgroundSize="cover"
          backgroundRepeat="no-repeat"
          pt={{ base: `${headerHeight + 40}px`, lg: `${headerHeight + 80}px` }}
          pb="40px"
          px="20px"
        >
          <Flex
            className={clsx({
              "text-black-01": headerTheme === "dark",
              "text-purple-gray-02": headerTheme === "light"
            })}
            justify="space-between"
            align="flex-start"
            direction={{ base: "column", lg: "row" }}
            maxW="1200px"
            mx="auto"
            gap={resource.imageSizeToggle ? "35px" : "40px"}
          >
            {/* Left Column */}
            <Stack
              direction="column"
              spacing="32px"
              maxW={`${columnWidths.heading}px}`}
            >
              <Breadcrumb
                color={ headerTheme === "dark"
                  ? "black-01"
                  : resource?.showHeroImage ? "white-01" : "bone-03"
                }
                separator={
                  <Box
                    color={
                      headerTheme === "dark" ? "black-01" : "purple-gray-02"
                    }
                  >
                    /
                  </Box>
                }
              >
                <BreadcrumbItem>
                  <BreadcrumbLink href={breadcrumbHref}>
                    {pageEntry.title}
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem hidden={!resource.resourceTypes.length}>
                  <BreadcrumbLink href={`/${resource.resourceTypes[0]?.uri}`}>
                    {resource.resourceTypes[0]?.title}
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage hidden>
                  <BreadcrumbLink href={resource.url}>
                    {resource.title}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </Breadcrumb>
              <Heading
                size="h1"
                className={"pr-3 whitespace-pre-line"}
                color={ resource?.showHeroImage && resource?.headingColor
                  ? resource?.headingColor
                  : headerTheme === "dark" ? "purple-01" : "green-05"
                }
              >
                {resource?.heading || resource.title}
              </Heading>
              {resource.resourceTypes[0].slug === "podcasts" ? (
                <Box fontSize={{ base: 16, lg: 20 }} className="whitespace-pre-wrap">{resource.episodeDetails}</Box>
              ) : (
                <Box fontSize={{ base: 16, lg: 20 }} className="whitespace-pre-wrap">{resource.summary}</Box>
              )}
              {/* Button Display Logic */}
              {!isGated && 
                (resource.target.url ||
                  resource.secondaryTarget.url ||
                  resource.downloadableFiles) &&  (
                  <Stack align="flex-start" direction="column" spacing="16px">
                    {resource.target.url && (
                      <SecondaryButton
                        target={resource.target}
                        legacyBackgroundColor={`${
                          headerTheme === "dark" ? "#6863F2" : "#B2F5E2"
                        }`}
                        textColor={`${
                          headerTheme === "dark" ? "#fff" : "#0D1418"
                        }`}
                      />
                    )}
                    {!resource.target.url && resource.downloadableFiles && (
                      <FileDownloadButtons
                        files={resource.downloadableFiles}
                        alignment="left"
                        legacyBackgroundColor={`${
                          headerTheme === "dark" ? "#6863F2" : "#B2F5E2"
                        }`}
                        textColor={`${
                          headerTheme === "dark" ? "#fff" : "#0D1418"
                        }`}
                      />
                    )}
                    {resource.secondaryTarget.url &&
                      resource.secondaryTarget.type && (
                        <SecondaryButton
                          target={resource.secondaryTarget}
                          legacyBackgroundColor={"#B9BAED"}
                          textColor="#0D1418"
                        />
                      )}
                  </Stack>
                )}
                {!isGated && resource.resourceTypes[0].slug === "podcasts" && (
                  <div>
                    <audio controls style={{
                      width: "100%",
                    }}>
                      <source src={resource.audioFileUrl} type="audio/mp3"></source>
                    </audio>
                  </div>
                )}
               {/* End Button Display Logic */}
            </Stack>
            {/* Right Column */}
            <Box backgroundColor="white" maxW={`${columnWidths.headingRight}px`} minW={{ base: "auto", lg: "363px" }}>
              {resource?.image[0] && resource.image[0]?.entryThumb && (
                <Image
                  src={process.env.NODE_ENV === "production" ? resource.image[0].entryThumb[0].url : resource.image[0].url}
                  height={columnWidths.headingRight}
                  width={columnWidths.headingRight}
                  alt={resource.title}
                  className={clsx("EntryThumbnail__image")}
                  draggable={false}
                  blurDataURL={resource.image[0].entryThumb[0].url}
                />
              )}
            </Box>
          </Flex>
        </Box>
        {/* Entry Content */}
        <Box px="20px">
          <Flex
            justify="space-between"
            align="flex-start"
            direction={{ base: "column", lg: "row" }}
            maxW="1200px"
            mx="auto"
            py="54px"
            gap="40px"
          >
            {/* Left Column */}
            <Stack
              className={clsx({ "mx-auto": !resource.showSidebar })}
              direction="column"
              spacing="32px"
              w="100%"
              maxW={`${columnWidths.left}px}`}
            >
              <AnimatePresence>
                {!manualAuth && isGated && accessGranted && (
                  <div className={clsx("mb-7.5")}>
                    <Message message={pageEntry.successMessage} />
                  </div>
                )}
              </AnimatePresence>
              {isGated && !accessGranted && (
                <ContentSections sections={visibleBeforeAccessSections} />
              )}
              {isGated && accessGranted && (
                <ContentSections sections={visibleAfterAccessSections} />
              )} 
              {!isGated && <ContentSections sections={allSections} />}
              {resource.speakers && resource.speakers.length > 0 && (
                <Speakers speakers={resource.speakers} title={resource.resourceTypes[0]?.slug == "podcasts" ? "Guests" : "Speakers"} />
              )}
              {resource.isc2Resource && (
                <div className="isc2CreditBox__Wrapper p-10 w-full">
                  {
                    <div className="isc2CreditBox">
                      <Image
                        src="/images/icons/ISC2-hq.png"
                        width={70}
                        height={28}
                        alt="Need an image here"
                        className="mb-7"
                      />
                      {!resource.isc2HourlyCredits ? (
                        <p>{`After viewing this resource, you are eligible for ${
                          resource.isc2Credits
                        } CPE credit${
                          resource.isc2Credits > 1 ? "s" : ""
                        } through ISC2.`}</p>
                      ) : (
                        <p>{`After viewing this resource, you are eligible for ${
                          resource.isc2HourlyCredits
                        } ISC2 CPE credit${
                          resource.isc2HourlyCredits > 1 ? "s" : ""
                        } for each hour viewed, for a total of up to ${
                          resource.isc2Credits
                        } credit${resource.isc2Credits > 1 ? "s" : ""}.`}</p>
                      )}
                    </div>
                  }
                </div>
              )}
            </Stack>
            {/* Right Column */}
            {resource.showSidebar && (
              <Stack
                className="sticky top-[100px]"
                direction="column"
                spacing="24px"
                w="100%"
                maxW={`${columnWidths.right}px}`}
              >
                {isGated && !accessGranted && !!pageEntry.form && (
                  <div className={clsx("bg-green-06 text-black-01 p-7.5")}>
                    <div className={clsx("mb-6")}>
                      <Heading size="h3">
                        {(resource?.overrideDefaultFormValues && resource?.formHeading)
                          || (resourceType === "video" ? "Watch Now" : "Download Now")}
                      </Heading>
                    </div>
                    <div className={clsx("mb-5")}>
                      <BodyText text={resource?.overrideDefaultFormValues && resource?.formDescription || pageEntry.formDescription} />
                    </div>
                    <EmbeddedForm
                      form={formEntry}
                      formData={formData}
                      formValues={formValues}
                      onSuccess={handleFormSuccess}
                      overrideButtonText={
                        (resource?.overrideDefaultFormValues && resource?.buttonText)
                          || (resourceType === "video" ? "Watch" : "Download")
                      }
                    />
                  </div>
                )}
                {/* Podcast Icons / Component */}
                {resource.resourceTypes[0].slug === "podcasts" && resource.showPodcastPlatformLogos ? (
                  <PodcastLogoBlock 
                    applePodcastLogo={pageEntry.applePodcastLogo && pageEntry.applePodcastLogo.length > 0 ? pageEntry.applePodcastLogo[0].url : null}
                    applePodcastLink={resource.podcastAppleUrl}
                    spotifyPodcastLogo={pageEntry.spotifyPodcastLogo && pageEntry.spotifyPodcastLogo.length > 0 ? pageEntry.spotifyPodcastLogo[0].url : null}   
                    spotifyPodcastLink={resource.podcastSpotifyUrl}
                    amazonPodcastLogo={pageEntry.amazonPodcastLogo && pageEntry.amazonPodcastLogo.length > 0 ? pageEntry.amazonPodcastLogo[0].url : null}
                    amazonPodcastLink={resource.podcastAmazonUrl}
                  />
                  ) : null
                }
                <SidebarCalloutsComponent
                  sidebarCallouts={resource.sidebarCallouts}
                />
                {resource.logo[0] && (
                  <Image
                    src={resource.logo[0].url}
                    alt={resource.logo[0].url}
                    width={0}
                    height={0}
                    sizes="400px"
                    style={{ width: "100%", height: "auto" }}
                  />
                )}
              </Stack>
            )}
          </Flex>
        </Box>
        <PlasmicComponent
          component="BladesDynamicDemoBlade"
        />
        {!!filteredResources && (
          <PlasmicComponent
            component="BladesDynamicRelatedContentManualEntries"
            componentProps={{ entries: filteredResources }}
            projectId={process.env.NEXT_PUBLIC_PLASMIC_DESIGN_SYSTEM_PROJECT_ID}
          />
        ) /*|| (<Spacer size="xs"/>)*/}
      </Box>
    </PlasmicRootProvider>
  );
}
